import { ClinicalEventEvidence } from './ClinicalEventEvidence';
import { customValueIssues, type CustomObservationDefinition } from './customDefinitions';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermissions } from '@/features/permissions';
import { useEventActions } from './api';
import { eventHeads, displayPayload } from './projection';
import { validatePayload } from './validate';
import type { ClinicalEvent, ClinicalSubject } from './types';
let sequence = 0;
const device = crypto.randomUUID();
export function replayIdentity() {
  return { device_id: device, client_seq: ++sequence };
}
export function EventHistory({
  episodeId,
  events,
  subjects,
  lateEntryMinutes,
  customDefinitions = [],
  editRequest,
}: {
  episodeId: string;
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  lateEntryMinutes?: number;
  customDefinitions?: CustomObservationDefinition[];
  editRequest?: ClinicalEvent;
}) {
  const { permissions } = usePermissions();
  const action = useEventActions(episodeId);
  const retry = useRef<{ fingerprint: string; identity: ReturnType<typeof replayIdentity> } | null>(null);
  useEffect(() => {
    if (editRequest && permissions.includes('amend_clinical_event')) choose(editRequest, 'amend');
  }, [editRequest]);
  const [selected, setSelected] = useState<{
    event: ClinicalEvent;
    kind: 'amend' | 'strike' | 'review' | 'cosign' | 'sign';
  } | null>(null);
  const [reason, setReason] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const rows = showHistory ? [...events].sort((a, b) => b.sequence - a.sequence) : eventHeads(events).reverse();
  function choose(event: ClinicalEvent, kind: 'amend' | 'strike' | 'review' | 'cosign' | 'sign') {
    retry.current = null;
    setSelected({ event, kind });
    setValues(
      Object.fromEntries(
        Object.entries(event.payload).map(([k, v]) => [
          k,
          v !== null && typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v),
        ]),
      ),
    );
    const time = new Date(event.occurred_at);
    setEventTime(new Date(time.getTime() - time.getTimezoneOffset() * 60000).toISOString().slice(0, 23));
    setReason('');
    setErrors([]);
  }
  async function submit() {
    if (!selected) return;
    const { event, kind } = selected;
    let payload: Record<string, unknown>;
    try {
      payload = Object.fromEntries(
        Object.entries(values)
          .filter(([, v]) => v.trim())
          .map(([k, v]) => [
            k,
            typeof event.payload[k] === 'number'
              ? Number(v)
              : typeof event.payload[k] === 'boolean'
                ? v === 'true'
                : event.payload[k] !== null && typeof event.payload[k] === 'object'
                  ? JSON.parse(v)
                  : v,
          ]),
      );
    } catch {
      setErrors(['Structured findings must contain valid JSON. Your original entry is unchanged.']);
      return;
    }
    const custom = customDefinitions.find(
      (d) => d.event_type === event.event_type && d.version === event.schema_version,
    );
    const issues =
      kind === 'amend'
        ? event.event_type.startsWith('CUSTOM.')
          ? custom
            ? customValueIssues(custom, payload.value)
            : ['Published custom schema unavailable; reload the practice configuration.']
          : validatePayload(event.event_type, payload, event.schema_version)
        : [];
    if (!reason.trim()) issues.push('Enter a reason.');
    if (kind === 'amend' && (!eventTime || !Number.isFinite(new Date(eventTime).getTime())))
      issues.push('Enter a valid observation time.');
    setErrors(issues);
    if (issues.length) return;
    const fingerprint = JSON.stringify({ root: event.root_id, head: event.id, kind, reason, payload, eventTime });
    if (retry.current?.fingerprint !== fingerprint) retry.current = { fingerprint, identity: replayIdentity() };
    try {
      await action.mutateAsync({
        rootId: event.root_id,
        input: {
          ...retry.current.identity,
          kind,
          expected_head_id: event.id,
          reason,
          ...(kind === 'amend' ? { payload, occurred_at: new Date(eventTime).toISOString() } : {}),
        },
      });
      setSelected(null);
    } catch {
      setErrors(['Could not save the action. Refresh to check for another author’s update; your reason is retained.']);
    }
  }
  return (
    <div className="flex flex-col gap-4">
      <Button variant="outline" className="min-h-11" onClick={() => setShowHistory(!showHistory)}>
        {showHistory ? 'Show current entries' : 'Show full audit history'}
      </Button>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Observed / recorded</TableHead>
            <TableHead>Subject / observation</TableHead>
            <TableHead>Finding</TableHead>
            <TableHead>Attribution</TableHead>
            <TableHead>Status / action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((event) => (
            <TableRow key={event.id}>
              <TableCell>
                {new Date(event.occurred_at).toLocaleString()}
                <div className="text-muted-foreground">Recorded {new Date(event.recorded_at).toLocaleString()}</div>
                {lateEntryMinutes !== undefined &&
                  Date.parse(event.recorded_at) - Date.parse(event.occurred_at) > lateEntryMinutes * 60000 && (
                    <Badge variant="outline">Late entry</Badge>
                  )}
              </TableCell>
              <TableCell>
                {subjects.find((s) => s.id === event.subject_id)?.label}
                <div>{event.event_type}</div>
              </TableCell>
              <TableCell>
                {displayPayload(event.payload)}
                <ClinicalEventEvidence
                  source={event.source_ref}
                  canReadDocument={permissions.includes('read_document')}
                />
                {event.reason && <div>Reason: {event.reason}</div>}
              </TableCell>
              <TableCell>
                {event.author_initials} · {event.author_role}
                <div>{event.source}</div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{event.status}</Badge>
                  <Badge variant="outline">{event.signed ? 'Signed' : 'Unsigned'}</Badge>
                  {!showHistory && event.status !== 'STRUCK' && (
                    <>
                      {event.status === 'ACTIVE' && !event.signed && permissions.includes('sign_clinical_event') && (
                        <Button size="sm" className="min-h-11" onClick={() => choose(event, 'sign')}>
                          Sign observation
                        </Button>
                      )}
                      {event.status === 'PENDING_COSIGN' && permissions.includes('cosign_clinical_event') && (
                        <Button size="sm" className="min-h-11" onClick={() => choose(event, 'cosign')}>
                          Co-sign observation
                        </Button>
                      )}
                      {permissions.includes('amend_clinical_event') && (
                        <Button size="sm" variant="outline" className="min-h-11" onClick={() => choose(event, 'amend')}>
                          Amend
                        </Button>
                      )}
                      {permissions.includes('strike_clinical_event') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="min-h-11"
                          onClick={() => choose(event, 'strike')}>
                          Strike
                        </Button>
                      )}
                      {event.status === 'PENDING_REVIEW' && permissions.includes('review_clinical_event') && (
                        <Button size="sm" className="min-h-11" onClick={() => choose(event, 'review')}>
                          Confirm review
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open && !action.isPending) setSelected(null);
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selected?.kind === 'amend'
                ? 'Amend observation'
                : selected?.kind === 'strike'
                  ? 'Strike observation'
                  : selected?.kind === 'cosign'
                    ? 'Co-sign observation'
                    : selected?.kind === 'sign'
                      ? 'Sign observation'
                      : 'Review observation'}
            </DialogTitle>
            <DialogDescription>The original entry remains in audit history.</DialogDescription>
          </DialogHeader>
          <FieldGroup>
            {selected?.kind === 'amend' && (
              <Field>
                <FieldLabel htmlFor="amend-observation-time">Observation time (this device’s local time)</FieldLabel>
                <Input
                  className="min-h-11"
                  id="amend-observation-time"
                  type="datetime-local"
                  step="0.001"
                  value={eventTime}
                  onChange={(e) => setEventTime(e.target.value)}
                />
              </Field>
            )}
            {selected?.kind === 'amend' &&
              Object.entries(values).map(([key, value]) => (
                <Field key={key}>
                  <FieldLabel htmlFor={`amend-${key}`}>{key.replaceAll('_', ' ')}</FieldLabel>
                  {typeof selected.event.payload[key] === 'boolean' ? (
                    <select
                      id={`amend-${key}`}
                      className="min-h-11 rounded border p-2"
                      value={value}
                      onChange={(e) => setValues({ ...values, [key]: e.target.value })}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (selected.event.payload[key] !== null && typeof selected.event.payload[key] === 'object') ||
                    (typeof selected.event.payload[key] === 'string' && (key === 'text' || value.includes('\n'))) ? (
                    <Textarea
                      id={`amend-${key}`}
                      value={value}
                      onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                    />
                  ) : (
                    <Input
                      className="min-h-11"
                      id={`amend-${key}`}
                      type={typeof selected.event.payload[key] === 'number' ? 'number' : 'text'}
                      step="any"
                      value={value}
                      onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                    />
                  )}
                </Field>
              ))}
            <Field>
              <FieldLabel htmlFor="action-reason">Reason</FieldLabel>
              <Input id="action-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </FieldGroup>
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>{errors.join(' ')}</AlertDescription>
            </Alert>
          )}
          <Button disabled={action.isPending} onClick={() => void submit()}>
            Save action
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
